import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import type { GameState, HomeOrAway, ScoreEvent } from '../types';

interface BaseProps {
  gameId: string;
  homeOrAway?: HomeOrAway;
  opponentName?: string;
  teamName?: string;
}

interface LiveProps extends BaseProps {
  mode: 'LIVE';
  /** Defaults to 3000ms — single DDB GetItem on the public state endpoint. */
  pollIntervalMs?: number;
}

interface VodProps extends BaseProps {
  mode: 'VOD';
  /** The <video> element to listen to for timeupdate. The overlay projects
   *  state as of the current playback time. */
  videoEl: HTMLVideoElement | null;
}

// LiveProps + VodProps are used via the per-mode exported components below.

function ScoreOverlayInner({
  homeOrAway,
  opponentName,
  teamName,
  state,
}: BaseProps & { state: GameState | undefined }) {
  if (!state || state.eventCount === 0) return null;

  const ourSide: 'HOME' | 'AWAY' = homeOrAway === 'AWAY' ? 'AWAY' : 'HOME';
  const ourRuns = ourSide === 'HOME' ? state.runsHome : state.runsAway;
  const themRuns = ourSide === 'HOME' ? state.runsAway : state.runsHome;

  const usLabel = teamName ?? 'Home';
  const themLabel = opponentName ?? 'Away';

  return (
    <div
      aria-label="Live score"
      className="pointer-events-none absolute left-3 top-3 select-none rounded-lg bg-black/70 px-3 py-2 text-white shadow-lg backdrop-blur-sm"
    >
      <div className="flex items-baseline gap-3">
        <span className="text-xs uppercase tracking-wider text-zinc-300">
          {usLabel}
        </span>
        <span className="font-mono text-2xl font-bold tabular-nums">{ourRuns}</span>
      </div>
      <div className="flex items-baseline gap-3">
        <span className="text-xs uppercase tracking-wider text-zinc-300">
          {themLabel}
        </span>
        <span className="font-mono text-2xl font-bold tabular-nums">{themRuns}</span>
      </div>
      <div className="mt-1 flex items-center gap-3 text-xs text-zinc-300">
        <span>
          {state.halfInning === 'TOP' ? '▲' : '▼'} {state.inning}
        </span>
        <span className="font-mono tabular-nums">
          {state.balls}–{state.strikes}
        </span>
        <span>
          {state.outs} out{state.outs === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );
}

/**
 * LIVE mode: poll /games/:id/state every 3s.
 */
export function LiveScoreOverlay(props: Omit<LiveProps, 'mode'>) {
  const { gameId, pollIntervalMs = 3000 } = props;
  const [state, setState] = useState<GameState | undefined>();

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const s = await api.gameState(gameId);
        if (!cancelled) setState(s);
      } catch {
        // ignore; keep polling
      }
      if (!cancelled) timer = setTimeout(() => void tick(), pollIntervalMs);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [gameId, pollIntervalMs]);

  return <ScoreOverlayInner {...props} state={state} />;
}

/**
 * VOD mode: pre-fetch all events on mount, project state at video.currentTime
 * by walking the event list and picking the latest stateAfter whose
 * videoTimestampSec <= currentTime. If events don't carry videoTimestampSec
 * (e.g., game scored without a broadcast linked), falls back to the final
 * state for the whole video.
 */
export function VodScoreOverlay({
  gameId,
  homeOrAway,
  opponentName,
  teamName,
  videoEl,
}: Omit<VodProps, 'mode'>) {
  const [events, setEvents] = useState<ScoreEvent[]>([]);
  const [tick, setTick] = useState(0); // forces re-render on timeupdate

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.gameEvents(gameId);
        if (!cancelled) setEvents(res.events);
      } catch {
        // ignore — overlay just renders nothing on failure
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  // Subscribe to video timeupdate.
  useEffect(() => {
    if (!videoEl) return;
    const handler = () => setTick((t) => t + 1);
    videoEl.addEventListener('timeupdate', handler);
    return () => videoEl.removeEventListener('timeupdate', handler);
  }, [videoEl]);

  const projected = useMemo<GameState | undefined>(() => {
    if (!videoEl) return events.at(-1)?.stateAfter;
    if (events.length === 0) return undefined;
    const t = videoEl.currentTime;
    // Find the latest event whose videoTimestampSec <= t.
    let best: ScoreEvent | undefined;
    for (const e of events) {
      if (e.videoTimestampSec === undefined) continue;
      if (e.videoTimestampSec <= t) best = e;
      else break;
    }
    if (best) return best.stateAfter;
    // No events have videoTimestampSec set, OR none yet at this point in
    // playback. Show the final state in the absence of timeline info; if
    // events do have timestamps, render nothing until t catches up to the
    // first one.
    const anyTimestamped = events.some((e) => e.videoTimestampSec !== undefined);
    return anyTimestamped ? undefined : events.at(-1)?.stateAfter;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, tick, videoEl]);

  return (
    <ScoreOverlayInner
      gameId={gameId}
      homeOrAway={homeOrAway}
      opponentName={opponentName}
      teamName={teamName}
      state={projected}
    />
  );
}

/**
 * Wraps a player and overlays the score. Auto-picks LIVE mode (polling)
 * or VOD mode (timeline-synced) based on whether `videoEl` is provided.
 */
export function PlayerWithOverlay({
  gameId,
  homeOrAway,
  opponentName,
  teamName,
  videoEl,
  children,
}: BaseProps & {
  videoEl?: HTMLVideoElement | null;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      {children}
      {videoEl !== undefined ? (
        <VodScoreOverlay
          gameId={gameId}
          homeOrAway={homeOrAway}
          opponentName={opponentName}
          teamName={teamName}
          videoEl={videoEl}
        />
      ) : (
        <LiveScoreOverlay
          gameId={gameId}
          homeOrAway={homeOrAway}
          opponentName={opponentName}
          teamName={teamName}
        />
      )}
    </div>
  );
}
